import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { Check, Copy, Link2, Loader2, RefreshCw, Users, X } from 'lucide-react';

import ShareChatModal from '../chat/ShareChatModal';
import {
  createShareLink,
  listShareLinks,
  listSnapshots,
  refreshShareLink,
  revokeShareLink,
} from '../../services/dashboardApi';

const EXPIRY = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: null, label: 'Until revoked' },
];

const when = (iso) =>
  new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const linkStatus = (link, now = new Date()) => {
  if (link.revoked_at) return 'revoked';
  if (link.expires_at && new Date(link.expires_at) <= now) return 'expired';
  return 'active';
};

export const publicUrl = (token) => `${window.location.origin}/d/${token}`;

/** Share a dashboard: read-only with teammates, or as a public snapshot link. */
const SharePanel = ({ accessToken, dashboard, onClose }) => {
  const [links, setLinks] = useState(null);
  const [expiry, setExpiry] = useState(30);
  const [busy, setBusy] = useState(null);
  const [created, setCreated] = useState(null); // { id, url }: shown once
  const [copied, setCopied] = useState(false);
  const [teammates, setTeammates] = useState(false);
  // '' = the data as it is now; else a saved snapshot's id.
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await listShareLinks(accessToken, dashboard.id);
      setLinks(res?.data?.links || []);
    } catch (e) {
      toast.error(e?.message || String(e));
      setLinks([]);
    }
  }, [accessToken, dashboard.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    listSnapshots(accessToken, dashboard.id)
      .then((res) => setSnapshots(res?.data || []))
      .catch(() => setSnapshots([]));
  }, [accessToken, dashboard.id]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !teammates && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, teammates]);

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const create = () =>
    run('create', async () => {
      const res = await createShareLink(
        accessToken,
        dashboard.id,
        expiry,
        snapshotId || null,
      );
      setCreated({ id: res.data.id, url: publicUrl(res.data.token) });
      setCopied(false);
      await load();
    });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
    } catch {
      toast.error('Could not copy. Select the link and copy it.');
    }
  };

  if (teammates) {
    return (
      <ShareChatModal
        item={{ type: 'dashboard', id: dashboard.id, title: dashboard.title }}
        accessToken={accessToken}
        onClose={() => setTeammates(false)}
      />
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-label="Share dashboard"
        className="flex max-h-[90vh] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 p-4 text-zinc-200 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Share {dashboard.title}</h2>
          <button onClick={onClose} title="Close" className="p-1 text-zinc-400">
            <X size={16} />
          </button>
        </div>

        <button
          onClick={() => setTeammates(true)}
          className="flex items-center gap-3 rounded-lg border border-zinc-700 px-3 py-2 text-left hover:bg-zinc-800"
        >
          <Users size={16} className="text-zinc-400" aria-hidden="true" />
          <span>
            <span className="block text-xs font-medium">
              Share with teammates
            </span>
            <span className="block text-[11px] text-zinc-500">
              They open it read-only and see data their own access allows.
            </span>
          </span>
        </button>

        <section className="flex flex-col gap-2">
          <h3 className="flex items-center gap-2 text-xs font-medium">
            <Link2 size={14} aria-hidden="true" /> Public link
          </h3>
          <p className="text-[11px] leading-snug text-zinc-500">
            Anyone with the link sees a fixed snapshot, without signing in. It
            does not update on its own and never shows queries. Update or revoke
            it here at any time. For live data, share with teammates.
          </p>
          {snapshots.length > 0 && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="link-source"
                className="text-[11px] text-zinc-400"
              >
                Shows
              </label>
              <select
                id="link-source"
                value={snapshotId}
                onChange={(e) => setSnapshotId(e.target.value)}
                className="min-w-0 flex-1 rounded-lg border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs"
              >
                <option value="">The data as it is now</option>
                {snapshots.map((snap) => (
                  <option key={snap.id} value={snap.id}>
                    Snapshot: {snap.name} · {when(snap.created_at)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label htmlFor="link-expiry" className="text-[11px] text-zinc-400">
              Expires
            </label>
            <select
              id="link-expiry"
              value={expiry ?? ''}
              onChange={(e) =>
                setExpiry(e.target.value ? Number(e.target.value) : null)
              }
              className="rounded-lg border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs"
            >
              {EXPIRY.map((o) => (
                <option key={o.label} value={o.days ?? ''}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              onClick={create}
              disabled={busy !== null}
              className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy === 'create' && (
                <Loader2 size={12} className="animate-spin" />
              )}
              Create link
            </button>
          </div>

          {created && (
            <div className="rounded-lg border border-[#5652CF]/40 bg-[#5652CF]/10 p-2">
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  aria-label="New link"
                  value={created.url}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded border border-zinc-700 bg-zinc-950 px-2 py-1 font-mono text-[11px]"
                />
                <button
                  onClick={copy}
                  className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[11px] hover:bg-zinc-800"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-amber-300">
                Copy it now: the link is not shown again.
              </p>
            </div>
          )}

          {links === null && <Loader2 className="animate-spin text-zinc-500" />}
          {links?.length > 0 && (
            <ul className="flex flex-col divide-y divide-zinc-800 rounded-lg border border-zinc-800">
              {links.map((link) => {
                const status = linkStatus(link);
                return (
                  <li
                    key={link.id}
                    className="flex items-center gap-2 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs">
                        Made {when(link.created_at)}
                        <span
                          className={`ml-2 text-[11px] ${status === 'active' ? 'text-green-400' : 'text-zinc-500'}`}
                        >
                          {status}
                        </span>
                      </p>
                      <p className="text-[11px] text-zinc-500">
                        Snapshot {when(link.snapshot_at)}
                        {link.expires_at &&
                          status === 'active' &&
                          ` · expires ${when(link.expires_at)}`}
                      </p>
                    </div>
                    {status === 'active' && (
                      <>
                        <button
                          onClick={() =>
                            run(link.id, async () => {
                              await refreshShareLink(
                                accessToken,
                                dashboard.id,
                                link.id,
                              );
                              toast.success('Link now shows the current data');
                              await load();
                            })
                          }
                          disabled={busy !== null}
                          title="Update the snapshot to the dashboard as it is now"
                          aria-label="Update snapshot"
                          className="rounded-md border border-zinc-700 p-1.5 hover:bg-zinc-800 disabled:opacity-50"
                        >
                          <RefreshCw
                            size={12}
                            className={busy === link.id ? 'animate-spin' : ''}
                          />
                        </button>
                        <button
                          onClick={() =>
                            run(`revoke-${link.id}`, async () => {
                              await revokeShareLink(
                                accessToken,
                                dashboard.id,
                                link.id,
                              );
                              if (created?.id === link.id) setCreated(null);
                              await load();
                            })
                          }
                          disabled={busy !== null}
                          className="rounded-md border border-zinc-700 px-2 py-1 text-[11px] hover:bg-red-900/40 disabled:opacity-50"
                        >
                          Revoke
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>,
    document.body,
  );
};

export default SharePanel;
